/*
 * rcds-dealer.js — storefront dealer pricing + ordering for the rcds theme.
 *
 * ONE LOGIN (2026-09-24). A dealer signs in ONCE at https://dealer.rolacase.us
 * and www.rolacase.us recognises them from then on. There is no second sign-in,
 * no Shopify customer session anywhere in this path, and no App Proxy.
 *
 * HOW THE BRIDGE WORKS. The portal login sets its session cookie on
 * api.rolacase.us with NO Domain attribute, so the cookie is HOST-ONLY and
 * Shopify never receives it. www.rolacase.us and api.rolacase.us share the
 * registrable domain rolacase.us, so a fetch from a storefront page to the API
 * is SAME-SITE even though it is cross-origin, and SameSite=Lax only restricts
 * CROSS-site requests — the cookie rides along at credentials:"include". That is
 * the whole mechanism.
 *
 * ⛔ Multipass and B2B company accounts were both ruled out against the live
 * store: it is on Grow (shopifyPlus:false) and a Multipass probe answers HTTP
 * 422 "Multipass login is not enabled for this shop" (positive control: a bogus
 * route answers 404). Do not reach for either again.
 * ⛔ The old App Proxy path identified the shopper only by Shopify's signed
 * logged_in_customer_id, which needs the very Shopify session this design
 * removes — and Shopify has an open investigation into that parameter arriving
 * empty under new customer accounts. It is not a foundation to build on.
 *
 * THREE FILES, ONE SESSION (split 2026-09-24, the 1000-line rule). This file is
 * the session, the one batched price read and the product-page dealer block.
 * assets/rcds-dealer-prices.js paints cards and the main price;
 * assets/rcds-dealer-cart.js owns /cart and PLACE ORDER. Both read every price
 * through window.__rcdsDealerBridge (below), never from a book of their own.
 *
 * ⛔ GOVERNING PRINCIPLE: a WRONG price shown to a dealer is worse than NO dealer
 * price. Every failure in this file degrades to retail. The discount itself is
 * decided server-side from the live dealers row on every single request, so
 * nothing the browser sends can move a price. The ONE amendment lives in the
 * cart layer: a browser known to be a dealer never gets the retail CHECKOUT
 * back from a failure, only from a definitive retail answer (Jeko, 2026-09-24).
 *
 * ENDPOINT CONTRACT, read off the backend half (router.go + dealer_session_handler.go):
 *   GET {API}/api/v1/storefront/dealer-session/price?variant_ids=1,2,3
 *       200 { is_dealer, tier, discount_pct, min_order_cents?, prices:[{
 *             variant_id, msrp_cents, dealer_price_cents, base_cents?, card_price_cents? }],
 *             first5_cash?: { note }, card_price_enabled?, ach_saves? }
 *       ⛔ card_price_enabled / ach_saves / card_price_cents come only while the
 *       server offers ACH or card (DEALER_CARD_PRICE_ENABLED); card_price_cents
 *       is the server's own card unit, so no file here computes a card price.
 *       ⛔ msrp_cents is ALWAYS the live retail price the page prints (the
 *       engine's sanity check compares against it). base_cents is present only
 *       when the dealer base is not that price: the MAP, once the storefront
 *       sells at MSRP = MAP x 1.04 (PRICE_BASE_MAP_ENABLED). The tier comes off
 *       base_cents then, so the struck figure and the -X% badge use it and
 *       label it "MAP". Absent (switch off) everything reads as before.
 *       ⛔ min_order_cents is the floor the ORDER route will enforce, in minor
 *       units, and it is PER DEALER — a US dealer's FIRST order sits higher than
 *       their second while the bronze first-order gate is on, so it is read from
 *       the body on every price call and never written into this file or the
 *       Liquid. It is OMITTED when the server does not know one, and absent is
 *       treated as no floor: the button stays live and the server decides, which
 *       is exactly the behaviour that shipped before the field existed.
 *       ⛔ EVERY decline is a 200 with is_dealer:false and an empty price list —
 *       no cookie, an expired one, a suspended dealer and a wrong-store cookie
 *       are byte-identical on the wire on purpose, so the browser cannot probe
 *       for which. This file therefore treats is_dealer as the ONLY authority
 *       and never infers a dealer from the presence of numbers.
 *       The 401/403 branch below is defence in depth, not the normal path.
 *       400 is the one non-200: a malformed request (no variant ids, or more
 *       than the server's cap). That is a caller bug, not a pricing outcome.
 *       Renewal rides on this call: on success the server re-issues the cookie
 *       with a fresh sliding window and the SAME absolute cap — but NOT on a
 *       top-level document navigation, which it refuses to count as the dealer
 *       being active (dealerSessionRenewalAllowed). A fetch from a storefront
 *       page, which is the only kind this file makes, does renew.
 *   CORS: Access-Control-Allow-Origin https://www.rolacase.us, Allow-Credentials
 *       true, methods GET/POST/OPTIONS, and an explicitly mounted OPTIONS route.
 *
 *   POST {API}/api/v1/storefront/dealer-session/order
 *       body { lines:[{ variant_id, quantity, shown_unit_price_cents }],
 *              shown_subtotal_cents, payment_method? }   — every field but
 *              payment_method required, see THE PRICE ECHO in submitOrder
 *              (assets/rcds-dealer-cart.js). payment_method "ach"|"card" is sent
 *              only while card is offered, and is then required (400 without).
 *       200 { ok, quote_id, reference, status:"placed", total_cents,
 *             redirect_url }
 *       400 an echo field is missing, or a line the cart refuses (a $0
 *           placeholder variant)
 *       403 no cookie, expired, suspended, wrong store — or the write-origin
 *           check refusing a caller that is not https://www.rolacase.us
 *       409 TWO DIFFERENT SHAPES, and only the first is handled specially:
 *           { error:"price_changed", message, is_dealer:true, discount_pct,
 *             prices:[…], shown_subtotal_cents, server_subtotal_cents }
 *             — the price moved under the shopper; the cart layer re-prices in
 *               place and asks again, it is not a dead end.
 *           { code:"portal_cart_in_use", error:"Someone else at your
 *             dealership has an order in progress…" }
 *             — a colleague's unfinished PORTAL cart would have been destroyed
 *               by this order, so the backend refused instead. Matched on
 *               .code (NOT .error, which carries the sentence), handled by its
 *               own branch in the cart layer, and it must stay ABOVE the 401/403
 *               branch: a 409 is not an expired session and must never call
 *               endSession().
 *       503 the route is mounted but its dependencies are not wired, or the
 *           dealer login lookup could not be completed
 *       CORS: the same corsDealerSession policy as the price route, with its
 *       own explicitly mounted OPTIONS route.
 *
 *   router.go mounts it with the price route (POST only, write-origin check,
 *   its own rate limiter). ⛔ Never point it back at POST /api/v1/storefront/
 *   dealer-price: that App Proxy route knows a dealer only by Shopify's signed
 *   logged_in_customer_id, the second login this design removes.
 */
(function () {
  if (window.__rcdsDealerBooted) return;
  window.__rcdsDealerBooted = true;

  // ⛔ THE API BASE COMES FROM THE PAGE, AND THE PAGE DECIDES WHETHER TO PRINT
  // IT AT ALL. layout/theme.liquid prints the literal 'https://api.rolacase.us'
  // — one dealer bridge exists and it is the US one — but prints it only inside
  // a test that the rendering store really is the US store, and does not load
  // this asset otherwise. So this file carries no host of its own and cannot
  // aim the US portal anywhere; absent (a store that failed that test, an older
  // layout) leaves API === '' and the feature simply off, which is the retail
  // default and therefore safe.
  var API = (typeof window.RCDS_DEALER_API === 'string')
    ? window.RCDS_DEALER_API.replace(/\/+$/, '')
    : '';
  var PRICES_URL = API + '/api/v1/storefront/dealer-session/price';
  // Mounted POST-only alongside the price route; contract in the file header.
  var ORDERS_URL = API + '/api/v1/storefront/dealer-session/order';

  var PORTAL_ORDERS_URL = 'https://dealer.rolacase.us/orders';
  var PORTAL_LOGIN_URL = 'https://dealer.rolacase.us/login';

  // ⛔ Mirrors domain.IsPlaceholderPrice on the backend: below this a "price" is
  // a row nobody has filled in, not a cheap product. Distinct from the $5.00
  // affordance floor in snippets/rcds-buy-gate.liquid — that one decides whether
  // a product may be bought at all, this one decides whether a DEALER number is
  // real. Do not merge them.
  var PLACEHOLDER_CEILING_CENTS = 200;

  // How long a price read stays trustworthy. Past it we stop rendering and
  // forget the session rather than show a number we cannot vouch for.
  var PRICE_FRESH_MS = 15 * 60 * 1000;
  // Floor between refocus re-validations, so alt-tab drumming cannot loop.
  var REVALIDATE_MIN_GAP_MS = 30 * 1000;
  var API_TIMEOUT_MS = 8000;
  /*
   * ⛔ A PRICE READ IS GIVEN THE TIME ITS SIZE NEEDS: 8 s plus 0.9 s per id,
   * capped, so a slow but healthy answer lands and a hung one gives up. The
   * backend prices 50 ids in one Shopify call (about 0.4 s), so this is headroom.
   * While it waits, a product page shows retail and /cart holds the checkout.
   */
  var PER_ID_BUDGET_MS = 900;
  var READ_TIMEOUT_CAP_MS = 50 * 1000;
  function readTimeoutMs(idCount) {
    return Math.min(READ_TIMEOUT_CAP_MS, API_TIMEOUT_MS + PER_ID_BUDGET_MS * idCount);
  }

  var COLOR_ERR = '#B91C1C';
  var COLOR_OK = '#15803D';
  var COLOR_MUTED = '#6A7079';

  /*
   * ⛔ THE CURRENCY COMES FROM THE STORE, NOT FROM THIS FILE. On /cart these
   * strings are the ONLY currency a dealer sees (the retail prices are off), so
   * a bare '$' would read as AUD. layout/theme.liquid prints the rendering
   * store's own shop.money_format into window.RCDS_MONEY_FORMAT ('US${{amount}}').
   *
   * ⛔ AN ABSENT OR MALFORMED FORMAT IS THE RETAIL-DEGRADE CASE, NOT A REASON
   * TO GUESS. hasMoneyFormat() is checked before anything renders a dealer
   * number, so a page that cannot say what currency it is in shows the retail
   * prices it already had. A right number in the wrong currency is the same
   * class of failure as a wrong number.
   */
  var MONEY_FORMAT = (typeof window.RCDS_MONEY_FORMAT === 'string' &&
                      window.RCDS_MONEY_FORMAT.indexOf('{{amount}}') !== -1)
    ? window.RCDS_MONEY_FORMAT
    : '';

  function hasMoneyFormat() { return MONEY_FORMAT !== ''; }

  function fmt(cents) {
    if (!hasMoneyFormat()) return '';
    var d = (Number(cents) / 100).toFixed(2).split('.');
    d[0] = d[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // {{amount}} is Shopify's comma-thousands, two-decimal token, as built above.
    return MONEY_FORMAT.replace('{{amount}}', d.join('.'));
  }

  /*
   * ⛔ ONE PREDICATE, BOTH SURFACES (isRealDealerPrice below): the PDP and the
   * cart must never disagree about what a real price is, or a placeholder hidden
   * on one is priced into the other.
   * ⛔ ABSENT, ZERO, NEGATIVE AND UNPARSEABLE FLOORS ALL MEAN "NO FLOOR": a
   * stale-high floor would refuse an order the server would have accepted.
   */
  function readMinOrderCents(d) {
    var n = Number(d && d.min_order_cents);
    return (isFinite(n) && n > 0) ? Math.round(n) : 0;
  }

  // A positive whole number of cents, or 0 when absent/invalid.
  function wholeCents(v) {
    var n = Number(v);
    return (isFinite(n) && n > 0 && Math.floor(n) === n) ? n : 0;
  }
  // One book entry from a price in the price route's shape (the 409 body too).
  // card_price_cents is the server's card unit, sent only while card is on.
  function bookEntry(p) {
    return { msrp_cents: Number(p.msrp_cents), dealer_price_cents: Number(p.dealer_price_cents),
      base_cents: wholeCents(p.base_cents), card_price_cents: wholeCents(p.card_price_cents) };
  }
  // ACH or card is offered only when the server says so (card_price_enabled).
  function readCard(d) {
    return (d && d.card_price_enabled === true) ? { saves: String(d.ach_saves || '') } : null;
  }

  function isRealDealerPrice(p) {
    if (!p) return false;
    var msrp = Number(p.msrp_cents);
    var net = Number(p.dealer_price_cents);
    if (!isFinite(msrp) || !isFinite(net)) return false;
    if (msrp <= PLACEHOLDER_CEILING_CENTS) return false;
    return net > 0;
  }

  /* ------------------------------------------------------------------ *
   * Session state.
   *
   * ⛔ TWO DIFFERENT THINGS, and conflating them is what sank the previous
   * attempt at this file:
   *
   *   clearRenderedDealerState()  "take the dealer numbers off the screen".
   *                               Pure DOM. Runs on EVERY render including the
   *                               SUCCESS path, because a re-render must not
   *                               leave the previous variant's numbers behind.
   *
   *   forgetDealerSession()       "we no longer know this is a dealer".
   *                               Drops is_dealer, the price map and the
   *                               freshness stamp.
   *
   * Calling the second as a DOM sweep once rendered a $0.00 /cart total and
   * locked PLACE ORDER. Only a DEFINITIVE answer may forget a session: a 401/403 from the
   * API, or a price read that has aged past PRICE_FRESH_MS. A network blip, a
   * 429 or a CORS failure proves nothing, so it stops the rendering and leaves
   * the session alone for the next re-validation to confirm.
   * ------------------------------------------------------------------ */
  // ms at which the last price read was STARTED, by any caller. Stamped before
  // the request, never on completion: stamping on completion lets N rapid
  // refocuses all clear the floor while the first request is still in flight.
  var lastPriceReadStartedAt = 0;

  var rc = {
    isDealer: false,
    discountPct: 0,
    gateNote: '',
    minOrderCents: 0,     // 0 = the server named no floor; see readMinOrderCents
    card: null,           // readCard() of the read on the book: null = one price, no choice
    pricedAt: 0,          // ms of the last FULL successful price read (a merge never moves it)
    priceByVariant: {},
    askedAt: {},          // variant id -> ms it was last asked in a read that came back a dealer
    terms: ''             // dealerTerms() of the read on the book
  };

  function forgetDealerSession() {
    rc.isDealer = false;
    rc.discountPct = 0;
    rc.gateNote = '';
    rc.minOrderCents = 0;
    rc.card = null;
    rc.pricedAt = 0;
    rc.priceByVariant = {};
    rc.askedAt = {};
    rc.terms = '';
  }

  /*
   * ⛔ "A DEALER WAS CONFIRMED ON THIS PAGE" OUTLIVES AN AGED BOOK, AND ONLY A
   * DEFINITIVE ANSWER ENDS IT. forgetDealerSession() also runs when a book ages
   * past PRICE_FRESH_MS, which proves nothing about WHO is at the keyboard; the
   * /cart hold must still know it is holding for a dealer. endDealerSession()
   * is the definitive version: a 200 is_dealer:false, a 401 or a 403.
   */
  var dealerConfirmedHere = false;

  function endDealerSession() {
    forgetDealerSession();
    dealerConfirmedHere = false;
  }

  function hasFreshDealerPricing() {
    return rc.isDealer === true && rc.pricedAt > 0 && (Date.now() - rc.pricedAt) <= PRICE_FRESH_MS;
  }

  // OUTCOME_* is what a price read tells us, and it is the only thing allowed to
  // decide whether a session is forgotten.
  var OUTCOME_DEALER = 'dealer';   // 200, is_dealer true  -> render
  var OUTCOME_RETAIL = 'retail';   // 200 is_dealer false, or 401/403 -> definitive
  var OUTCOME_UNKNOWN = 'unknown'; // network / CORS / 429 / malformed -> proves nothing

  /* ------------------------------------------------------------------ *
   * "HAS THIS BROWSER EVER HAD DEALER PRICING?" — the one thing the server
   * cannot answer, so the only thing this file is allowed to remember.
   *
   * ⛔ THE FAILURE THIS CLOSES. A dealer signs in at dealer.rolacase.us, works
   * in the portal for an hour without opening the storefront, comes here and
   * sees retail with NO EXPLANATION AT ALL. The price route answers 200
   * is_dealer:false, the dealer block hides, and the page is byte-identical to
   * a stranger's (production, 2026-09-24: reason=no_session_cookie).
   * An expired cookie is DELETED by the browser, so "expired" and "never had
   * one" arrive at the server as the same empty Cookie header. The server
   * genuinely cannot tell them apart. The browser can.
   *
   * ⛔ IT DECIDES WHETHER TO SHOW A SENTENCE. NOTHING ELSE. No price, no
   * discount, no tier, no variant, ever goes in here or comes out of here. A
   * price on screen must have been read from the API in the last
   * PRICE_FRESH_MS; a stale number rendered out of a browser's memory is the
   * exact defect this whole bridge exists to prevent, and it would be worse
   * than the silence it replaces. What is stored is one integer: the
   * millisecond a dealer price read last SUCCEEDED.
   *
   * ⛔ EVERY READ AND EVERY WRITE IS WRAPPED. A private window, "block all
   * cookies", an extension, a full quota: each of these is NORMAL, not an
   * error, and each one throws somewhere different. Safari does not throw on
   * `window.localStorage` — it throws on the first getItem — so the probe below
   * touches the store rather than merely naming it. When anything throws the
   * answer is "cannot know", and "cannot know" renders the guest page, because
   * a guest being told nothing is right and a guest being nagged is not.
   *
   * ⛔ IT EXPIRES, SO AN EX-DEALER IS NOT NAGGED FOR EVER. The notice is only
   * useful to somebody who can act on it by signing in. A suspended dealer, or
   * a colleague on the branch counter who used this machine once, cannot, and
   * the portal lives on a different origin so it can never reach in here and
   * clear the flag itself. A week is the longest a lapse is still worth
   * mentioning: a real dealer has had every working day to come back, and
   * anyone who has not is simply shown the retail page from then on.
   * ------------------------------------------------------------------ */
  var SEEN_KEY = 'rcds.dealer.seen';               // value: ms of the last confirmed dealer price read
  var SEEN_MEMORY_MS = 7 * 24 * 60 * 60 * 1000;    // after this, stop mentioning it
  /*
   * ⛔ THE SAME MEMORY HOLDS THE CHECKOUT WHILE THE PAGE LOADS. An inline <head>
   * snippet in layout/theme.liquid reads SEEN_KEY with the SAME window and, when
   * it is set, puts PENDING_CLASS on <html>, which hides #rcds-checkout-btn
   * before any deferred script has run. The three names are mirrored BY NAME in
   * that snippet; change one and change both, test/cart-hold.js compares them.
   * The cart layer takes the class off once the price read has answered; a page
   * with no cart dealer block has nothing to hold, so boot() takes it off there.
   */
  var PENDING_CLASS = 'rcds-dealer-pending';
  // ⛔ THE PRICE HOLD (2026-09-25): layout/theme.liquid sets it in <head> to keep
  // retail figures invisible until dealer ones paint; named here, mirrored there.
  // Lifted via the bridge (price engine; cart layer on /cart); the <head> timer fails open.
  var PRICE_HOLD_CLASS = 'rcds-dealer-prices-pending';
  function releasePriceHold() {
    var root = document.documentElement;
    if (root && root.classList) root.classList.remove(PRICE_HOLD_CLASS);
  }

  // dealerMemory returns the store, or null when this browser will not lend us
  // one. Null is a normal answer and every caller treats it as "cannot know".
  function dealerMemory() {
    try {
      var s = window.localStorage;
      // ⛔ TOUCH IT, DO NOT JUST NAME IT. Reading the property succeeds under
      // "block all cookies"; the throw comes on first access.
      s.getItem(SEEN_KEY);
      return s;
    } catch (e) {
      return null;
    }
  }

  function rememberDealerSeen() {
    var s = dealerMemory();
    if (!s) return;
    try { s.setItem(SEEN_KEY, String(Date.now())); } catch (e) { /* quota / private mode */ }
  }

  function forgetDealerSeen() {
    var s = dealerMemory();
    if (!s) return;
    try { s.removeItem(SEEN_KEY); } catch (e) { /* nothing else to try */ }
  }

  // hadDealerPricing is the ONLY question the memory answers, and it answers
  // false whenever it is unsure — no store, no entry, an unreadable entry, a
  // clock that has moved backwards, or an entry older than the memory window.
  function hadDealerPricing() {
    var s = dealerMemory();
    if (!s) return false;
    var raw = null;
    try { raw = s.getItem(SEEN_KEY); } catch (e) { return false; }
    var at = parseInt(raw, 10);
    if (!isFinite(at) || at <= 0) return false;
    var age = Date.now() - at;
    if (age < 0 || age > SEEN_MEMORY_MS) { forgetDealerSeen(); return false; }
    return true;
  }

  /*
   * paintLapsedNotice puts ONE sentence beside a dealer block that is hiding,
   * or takes it away again. `anchor` is the dealer block itself; the notice is
   * appended as its SIBLING because the block is hidden on exactly the path
   * that needs explaining, and revealing it to carry a sentence would expose
   * the empty price slots inside it.
   *
   * ⛔ ONLY ON A DEFINITIVE ANSWER. OUTCOME_RETAIL is the server saying "there
   * is no dealer session here" — a 200 is_dealer:false, or a 401/403.
   * OUTCOME_UNKNOWN is a network blip, a 429, a CORS failure, an edge 502: it
   * proves nothing, and telling a dealer whose session is perfectly healthy
   * that it has ended is a lie they would act on. So an unknown says nothing,
   * the same way it renders nothing.
   * ⛔ AND ONLY WITH MEMORY. Without it this is a stranger, and a stranger sees
   * exactly the page the store served.
   */
  /*
   * ⛔ THIS SENTENCE IS THE ONLY NEW CUSTOMER-FACING COPY ON www.rolacase.us
   * AND SOPHIA HAS NOT SEEN IT. Reword it only after she has. A shorter draft
   * exists and lints clean, and it is deliberately NOT here: leaving it beside
   * the live string is how an unreviewed rewrite ships by accident.
   *
   * ⛔ THE RULES IT HAS TO KEEP, whoever edits it. No em-dash, no en-dash, no
   * exclamation mark, no "we", "us" or "our", and NEVER a digit — a number in
   * this sentence is a price claim made from a browser's memory rather than
   * from the server. The harness lints the RENDERED text on every run
   * ("LAPSED COPY:" and "the notice is a sentence, never a number"), strips
   * "rolacase.us" first so the domain does not register as the word "us", and
   * proves each rule can fire before it believes a count of zero.
   */
  var LAPSED_ATTR = 'data-rcds-dealer-lapsed';
  var LAPSED_HTML =
    'Dealer pricing is not active on this browser right now, so this page is ' +
    'showing retail prices. <a href="' + PORTAL_LOGIN_URL +
    '" style="color:#D81F1F;text-decoration:underline;">Sign in again at dealer.rolacase.us</a> ' +
    'to see your dealer prices.';

  function paintLapsedNotice(anchor, outcome) {
    if (!anchor) return;
    if (anchor.__rcdsLapsed) {
      try { anchor.__rcdsLapsed.remove(); } catch (e) { /* already detached */ }
      anchor.__rcdsLapsed = null;
    }
    if (outcome !== OUTCOME_RETAIL) return;
    if (!hadDealerPricing()) return;
    if (!anchor.parentNode) return;
    var el = document.createElement('div');
    el.className = 'rcds-dealer-lapsed';
    el.setAttribute(LAPSED_ATTR, '');
    el.setAttribute('role', 'status');
    el.setAttribute('style',
      'margin:12px 0;padding:12px 14px;border:1px solid rgba(216,31,31,0.25);' +
      'border-radius:10px;background:#FEF2F2;color:#7F1D1D;font-size:14px;line-height:1.5;');
    el.innerHTML = LAPSED_HTML;
    anchor.parentNode.appendChild(el);
    anchor.__rcdsLapsed = el;
  }

  /* ------------------------------------------------------------------ *
   * ONE READ PER PAGE, HOWEVER MANY SURFACES ASK (2026-09-24).
   *
   * loadPrices asks the API for MSRP + dealer price for the given variants.
   * It NEVER rejects and it NEVER touches the DOM; it only classifies what came
   * back and updates the session on a definitive answer. Callers decide what to
   * draw.
   *
   * ⛔ CALLS MADE IN THE SAME SYNCHRONOUS RUN BECOME ONE READ. The PDP dealer
   * block, the price engine (assets/rcds-dealer-prices.js: every card, the main
   * price, the sticky bar, quick view) and the /cart layer each ask for their
   * own variants; asked separately, a 24-card page would be 24 requests. So a
   * call JOINS the batch still collecting (ids de-duplicated), and the batch
   * leaves on the next microtask. That is also why
   * boot() runs every surface in one synchronous loop: a microtask checkpoint
   * runs between two event listeners and between two deferred scripts, so a
   * surface that booted in a separate task would have gone out alone.
   *
   * ⛔ AT MOST MAX_IDS_PER_READ IDS PER REQUEST, IN PARALLEL: above the cap
   * (usecase maxStorefrontPriceVariants) the route answers 400, a caller bug.
   *
   * ⛔ ALL OR NOTHING ACROSS CHUNKS. Every chunk must come back a dealer, and
   * every chunk must describe the SAME dealer terms (discount, tier, floor,
   * card offer, first-order note). Chunks straddling a tier edit would otherwise paint two
   * discounts on one page. Any definitive retail answer forgets the session,
   * unless a NEWER read has already confirmed a dealer; anything else proves
   * nothing and renders nothing.
   *
   * ⛔ A MERGE NEVER EXTENDS FRESHNESS. A surface that appears later (a quick
   * view, a swapped grid) asks for its missing ids with merge=true. The new
   * numbers join the book, but rc.pricedAt stays at the read that last
   * confirmed EVERYTHING on it, so no number can outlive PRICE_FRESH_MS by
   * riding on a younger neighbour. A full read (boot, re-validation, any
   * caller not asking to merge) replaces the book outright.
   * ------------------------------------------------------------------ */
  var MAX_IDS_PER_READ = 50;
  var pendingRead = null;   // the batch still collecting ids in this synchronous run
  var readSeq = 0;          // start order of batches
  var committedSeq = 0;     // the newest batch whose numbers are on the book

  function cleanVariantIds(list) {
    var out = [];
    (list || []).forEach(function (v) {
      var n = Number(v);
      if (isFinite(n) && n > 0 && Math.floor(n) === n) out.push(n);
    });
    return out;
  }

  function loadPrices(variantIds, merge) {
    var ids = cleanVariantIds(variantIds);
    if (!API || !ids.length) {
      return Promise.resolve(OUTCOME_UNKNOWN);
    }
    lastPriceReadStartedAt = Date.now();
    if (!pendingRead) {
      pendingRead = { ids: [], seen: {}, merge: true, done: [] };
      Promise.resolve().then(sendPriceRead);
    }
    var batch = pendingRead;
    if (merge !== true) batch.merge = false;
    ids.forEach(function (id) {
      if (!batch.seen[id]) { batch.seen[id] = true; batch.ids.push(id); }
    });
    return new Promise(function (resolve) { batch.done.push(resolve); });
  }

  function sendPriceRead() {
    var batch = pendingRead;
    pendingRead = null;
    if (!batch) return;
    var seq = ++readSeq;
    var chunks = [];
    for (var i = 0; i < batch.ids.length; i += MAX_IDS_PER_READ) {
      chunks.push(batch.ids.slice(i, i + MAX_IDS_PER_READ));
    }
    var finish = function (outcome) { batch.done.forEach(function (resolve) { resolve(outcome); }); };
    // A throw inside a chunk is a failure to know, never a hung surface.
    var fetchChunk = function (c) {
      return fetchPriceChunk(c).then(null, function () { return { outcome: OUTCOME_UNKNOWN }; });
    };
    // ⛔ A BROWSER WITH NO DEALER BEHIND IT SENDS ONE REQUEST. With no dealer
    // confirmed here and nothing remembered, the first chunk goes alone and the
    // rest follow only if it answers dealer. A guest on a big collection page
    // costs the API one call, as it did before chunking; a known dealer loses
    // nothing, and a fresh dealer pays one extra round trip once.
    var probeFirst = chunks.length > 1 && !dealerConfirmedHere && !hadDealerPricing();
    var run = probeFirst
      ? fetchChunk(chunks[0]).then(function (first) {
          if (first.outcome !== OUTCOME_DEALER) return [first];
          return Promise.all(chunks.slice(1).map(fetchChunk)).then(function (rest) { return [first].concat(rest); });
        })
      : Promise.all(chunks.map(fetchChunk));
    run.then(function (parts) { finish(settlePriceRead(parts, batch, seq)); })
      .then(null, function () { finish(OUTCOME_UNKNOWN); });
  }

  // The terms every chunk of one read must agree on. Prices may differ per
  // variant; WHO the dealer is, and on what terms, may not.
  function dealerTerms(d) {
    return JSON.stringify([
      parseFloat(d.discount_pct) || 0,
      String(d.tier || ''),
      readMinOrderCents(d),
      !!readCard(d),
      (d.first5_cash && d.first5_cash.note) ? String(d.first5_cash.note) : ''
    ]);
  }

  function settlePriceRead(parts, batch, seq) {
    var i;
    for (i = 0; i < parts.length; i++) {
      if (parts[i].outcome !== OUTCOME_RETAIL) continue;
      // ⛔ AN OLDER "NOT A DEALER" CANNOT OVERRULE A NEWER "DEALER". A read that
      // started before the one on the book, and answers retail after it, used
      // to end the session and release /cart's retail checkout under a dealer
      // total the newer read had just confirmed. It proves nothing now.
      if (seq < committedSeq) return OUTCOME_UNKNOWN;
      endDealerSession();
      return OUTCOME_RETAIL;
    }
    for (i = 0; i < parts.length; i++) {
      if (parts[i].outcome !== OUTCOME_DEALER || !parts[i].data) return OUTCOME_UNKNOWN;
    }
    var head = parts[0].data;
    var terms = dealerTerms(head);
    for (i = 1; i < parts.length; i++) {
      if (dealerTerms(parts[i].data) !== terms) return OUTCOME_UNKNOWN;
    }
    var fresh = {};
    parts.forEach(function (part) {
      part.data.prices.forEach(function (p) { fresh[p.variant_id] = bookEntry(p); });
    });
    var now = Date.now();
    // ⛔ A READ THAT STARTED BEFORE THE ONE ALREADY ON THE BOOK may carry the
    // older numbers. It may fill a gap; it may not overwrite, and it says
    // nothing new about the session.
    if (seq < committedSeq) {
      if (!hasFreshDealerPricing() || rc.terms !== terms) return OUTCOME_UNKNOWN;
      Object.keys(fresh).forEach(function (vid) {
        if (!rc.priceByVariant[vid]) rc.priceByVariant[vid] = fresh[vid];
      });
      batch.ids.forEach(function (id) { if (!rc.askedAt[id]) rc.askedAt[id] = now; });
      return OUTCOME_DEALER;
    }
    committedSeq = seq;
    var keepBook = batch.merge && hasFreshDealerPricing() && rc.terms === terms;
    var map = keepBook ? rc.priceByVariant : {};
    var asked = keepBook ? rc.askedAt : {};
    Object.keys(fresh).forEach(function (vid) { map[vid] = fresh[vid]; });
    batch.ids.forEach(function (id) { asked[id] = now; });
    rc.isDealer = true;
    rc.discountPct = parseFloat(head.discount_pct) || 0;
    rc.gateNote = (head.first5_cash && head.first5_cash.note) ? String(head.first5_cash.note) : '';
    rc.minOrderCents = readMinOrderCents(head);
    rc.card = readCard(head);
    rc.terms = terms;
    rc.priceByVariant = map;
    rc.askedAt = asked;
    rc.pricedAt = keepBook ? rc.pricedAt : now;
    dealerConfirmedHere = true;
    // ⛔ LAST, AND ONLY HERE. This is the one moment the API has confirmed
    // a dealer, so it is the one moment the browser may remember having
    // been one. It writes a timestamp and nothing else — see SEEN_KEY.
    rememberDealerSeen();
    return OUTCOME_DEALER;
  }

  /*
   * acceptPriceChange takes the body of the ORDER route's 409 price_changed,
   * which carries the new prices in the price route's own shape, and puts them
   * on the book so /cart can re-render with no second round trip (a re-fetch
   * could fail and leave "the price changed" beside no price at all).
   * ⛔ Whatever the body says about the floor, INCLUDING SAYING NOTHING, which
   * clears it rather than keeping a figure the server has just declined to
   * restate. A refusal that answered at all came from a live dealer session.
   */
  function acceptPriceChange(d) {
    if (!d || typeof d !== 'object' || !Array.isArray(d.prices)) return false;
    var map = {};
    var asked = {};
    var now = Date.now();
    d.prices.forEach(function (p) {
      map[p.variant_id] = bookEntry(p);
      asked[p.variant_id] = now;
    });
    rc.isDealer = true;
    rc.discountPct = parseFloat(d.discount_pct) || 0;
    rc.gateNote = (d.first5_cash && d.first5_cash.note) ? String(d.first5_cash.note) : '';
    rc.minOrderCents = readMinOrderCents(d);
    rc.card = readCard(d);
    rc.terms = dealerTerms(d);
    rc.priceByVariant = map;
    rc.askedAt = asked;
    rc.pricedAt = now;
    dealerConfirmedHere = true;
    return true;
  }

  /*
   * fetchPriceChunk is ONE request of at most MAX_IDS_PER_READ ids. It commits
   * NOTHING, a retail answer included: every answer is handed back for
   * settlePriceRead to weigh against the other chunks of the same read and
   * against newer reads, and only settlePriceRead may forget the session.
   */
  function fetchPriceChunk(variantIds) {
    var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, readTimeoutMs(variantIds.length)) : null;

    return fetch(PRICES_URL + '?variant_ids=' + variantIds.join(','), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      // ⛔ include, not same-origin: the cookie lives on api.rolacase.us, a
      // different ORIGIN but the same SITE, which is why it is sent at all.
      credentials: 'include',
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      // 401/403 is the server saying "there is no dealer session here". That is
      // an answer, not a failure: settlePriceRead forgets the session on it.
      if (r.status === 401 || r.status === 403) {
        return { outcome: OUTCOME_RETAIL };
      }
      if (!r.ok) return { outcome: OUTCOME_UNKNOWN };
      return r.json().then(function (data) {
        if (!data || typeof data !== 'object') return { outcome: OUTCOME_UNKNOWN };
        // ⛔ ONLY AN EXPLICIT is_dealer:false IS AN ANSWER. Any other shape (an
        // empty object, a dealer with no prices array) is a body something
        // between us and the API has damaged: it proves nothing, so a known
        // dealer holds instead of being handed the retail checkout.
        if (data.is_dealer === false) return { outcome: OUTCOME_RETAIL };
        if (data.is_dealer !== true || !Array.isArray(data.prices)) {
          return { outcome: OUTCOME_UNKNOWN };
        }
        return { outcome: OUTCOME_DEALER, data: data };
      }, function () { return { outcome: OUTCOME_UNKNOWN }; });
    }, function () {
      if (timer) clearTimeout(timer);
      return { outcome: OUTCOME_UNKNOWN }; // network, CORS, abort — proves nothing
    });
  }

  function updateCartCounts(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = n; });
    // The rcds header badge (snippets/rcds-nav.liquid) is hidden at zero, the
    // same way the theme's own badge code does it.
    document.querySelectorAll('[data-rcds-cart-count]').forEach(function (el) {
      el.textContent = String(n);
      if (n > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
  }

  /* ------------------------------------------------------------------ *
   * Re-validation. One listener, one in-flight flag, one floor, shared by every
   * surface on the page, so two surfaces cannot double-fire and a dealer
   * drumming alt-tab cannot loop the API.
   * ⛔ lastRevalidateStartedAt is stamped BEFORE the request goes out, not in the
   * .then — stamping on completion lets N rapid refocuses all pass the floor
   * while the first request is still in flight, which is the floor the previous
   * attempt claimed in a comment but did not implement.
   * ------------------------------------------------------------------ */
  var surfaces = [];
  var revalidateInFlight = false;

  function revalidateAll() {
    if (revalidateInFlight) return Promise.resolve(false);
    // ⛔ The floor is measured from the last price read by ANY caller, not from
    // the last refocus. Boot's own refresh() does not come through here, so a
    // floor that only counted refocuses let the very first one re-read a page
    // that had loaded a second earlier — a guard that did not guard.
    if ((Date.now() - lastPriceReadStartedAt) < REVALIDATE_MIN_GAP_MS) {
      return Promise.resolve(false);
    }
    revalidateInFlight = true;
    var done = function () { revalidateInFlight = false; return true; };
    return Promise.all(surfaces.map(function (s) { return s.refresh(); })).then(done, done);
  }

  document.addEventListener('visibilitychange', function () {
    // Catches expiry, suspension and a tier change while the tab was away.
    if (document.visibilityState === 'visible') revalidateAll();
  });

  /* ---- PDP buy-box -------------------------------------------------- */
  function bootBuyBox(node) {
    if (node.__rcdsWired) return;
    node.__rcdsWired = true;

    var scope = node.closest('.rcds-quote, .rcds-product-main, [data-section-type="product-custom-main"], .product-main-wrapper') || document;
    var allVariantIds = (node.getAttribute('data-variant-ids') || '').split(',').filter(Boolean).map(Number);

    var wasEl = node.querySelector('[data-rcds-was]');
    var netEl = node.querySelector('[data-rcds-net]');
    var tierEl = node.querySelector('[data-rcds-tier]');
    var noteEl = node.querySelector('[data-rcds-note]');
    var qtyEl = node.querySelector('[data-rcds-qty]');
    var addBtn = node.querySelector('[data-rcds-add]');
    var msgEl = node.querySelector('[data-rcds-msg]');

    function selectedVariantId() {
      var live = scope.querySelector('[data-rcds-variant-id]');
      var v = live ? parseInt(live.value, 10) : NaN;
      if (isFinite(v) && v > 0) return v;
      var fb = parseInt(node.getAttribute('data-fallback-variant'), 10);
      return isFinite(fb) && fb > 0 ? fb : null;
    }
    function selectedQty() {
      var q = qtyEl ? parseInt(qtyEl.value, 10) : 1;
      return isFinite(q) && q > 0 ? q : 1;
    }

    // Pure DOM sweep. Says nothing about whether a dealer session exists.
    function clearRenderedDealerState() {
      node.setAttribute('hidden', '');
      if (wasEl) { wasEl.textContent = ''; wasEl.setAttribute('hidden', ''); }
      if (tierEl) { tierEl.textContent = ''; tierEl.setAttribute('hidden', ''); }
      if (noteEl) { noteEl.textContent = ''; noteEl.setAttribute('hidden', ''); }
      if (netEl) netEl.textContent = '';
      if (msgEl) msgEl.textContent = '';
    }

    function render() {
      clearRenderedDealerState();
      // ⛔ ONE PRICE, ONE BUTTON. On a BUY product the price engine
      // (assets/rcds-dealer-prices.js) shows the dealer price in the main
      // price slot and relabels the native Add to Cart, so this block would be
      // a second copy of both. It stays hidden once the engine has CLAIMED the
      // scope, and only then: without the engine (an older layout, a failed
      // load) nothing claims anything and this block is the dealer price, as
      // before. A QUOTE product's panel is claimed too (decision 4): the engine
      // shows the dealer price there as information and Request a Quote stays
      // the one button, so this block, with its Add to order, never shows.
      if (isClaimedScope(scope)) return;
      // ⛔ NO CURRENCY, NO DEALER NUMBERS. Returning here leaves the retail
      // cart exactly as the server rendered it, which is the whole failure
      // contract of this file applied to one more input.
      if (!hasMoneyFormat()) return;
      if (!hasFreshDealerPricing()) {
        // Aged past the horizon: the cached numbers are no longer evidence.
        if (rc.isDealer && rc.pricedAt > 0) forgetDealerSession();
        return;
      }
      var vid = selectedVariantId();
      var p = vid ? rc.priceByVariant[vid] : null;
      if (!isRealDealerPrice(p)) return; // placeholder row -> quote only
      netEl.textContent = fmt(p.dealer_price_cents);
      // The struck figure is the base the tier came off: the MAP when the
      // server sends one, else the retail price (unchanged behaviour).
      var was = p.base_cents > 0 ? p.base_cents : p.msrp_cents;
      if (p.dealer_price_cents < was) {
        wasEl.textContent = (p.base_cents > 0 ? 'MAP ' : '') + fmt(was);
        wasEl.removeAttribute('hidden');
        if (rc.discountPct > 0) {
          tierEl.textContent = '-' + rc.discountPct + '%';
          tierEl.removeAttribute('hidden');
        }
      }
      if (rc.gateNote) {
        noteEl.textContent = rc.gateNote;
        noteEl.removeAttribute('hidden');
      }
      node.removeAttribute('hidden');
    }

    function refresh() {
      return loadPrices(allVariantIds).then(function (outcome) {
        // Says a sentence, or takes one away. Never a number; see
        // paintLapsedNotice. Placed before the branches below so the two
        // existing paths stay exactly as they were.
        paintLapsedNotice(node, outcome);
        if (outcome === OUTCOME_DEALER) { render(); return outcome; }
        // RETAIL: the session is already forgotten by loadPrices.
        // UNKNOWN: we could not confirm, so stop showing dealer numbers but
        // leave a healthy session intact for the next re-validation.
        clearRenderedDealerState();
        return outcome;
      });
    }

    function onAdd() {
      var vid = selectedVariantId();
      if (!vid) return;
      // ⛔ Never add against a price we can no longer vouch for.
      if (!hasFreshDealerPricing() || !isRealDealerPrice(rc.priceByVariant[vid])) {
        render();
        msgEl.innerHTML = 'Your dealer session has expired. <a href="' + PORTAL_LOGIN_URL + '" style="color:#D81F1F;text-decoration:underline;">Sign in again</a> to use dealer pricing.';
        msgEl.style.color = COLOR_ERR;
        return;
      }
      addBtn.disabled = true;
      var orig = addBtn.textContent;
      addBtn.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin', // Shopify's own cart, same origin as the page
        body: JSON.stringify({ items: [{ id: vid, quantity: selectedQty() }] })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          addBtn.disabled = false; addBtn.textContent = orig;
          if (!res.ok) {
            msgEl.innerHTML = (res.d && res.d.description) ? res.d.description : 'Could not add this item.';
            msgEl.style.color = COLOR_ERR;
            return;
          }
          msgEl.innerHTML = 'Added to your order. <a href="/cart" style="color:#D81F1F;text-decoration:underline;">Review &amp; place order &rarr;</a>';
          msgEl.style.color = COLOR_OK;
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { updateCartCounts(c.item_count); }).catch(function () {});
        })
        .catch(function () {
          addBtn.disabled = false; addBtn.textContent = orig;
          msgEl.textContent = 'Could not add this item. Please try again.';
          msgEl.style.color = COLOR_ERR;
        });
    }

    if (addBtn) addBtn.addEventListener('click', onAdd);
    scope.querySelectorAll('.rcds-variant__radio').forEach(function (r) {
      // Variant change re-reads the already-loaded map; no new API call.
      r.addEventListener('change', function () { setTimeout(render, 50); });
    });

    if (!allVariantIds.length) {
      var only = selectedVariantId();
      if (only) allVariantIds = [only];
    }
    surfaces.push({ refresh: refresh });
    refresh();
  }

  /* ------------------------------------------------------------------ *
   * THE BRIDGE to assets/rcds-dealer-prices.js (cards, the main PDP price, the
   * sticky bar, quick view) and assets/rcds-dealer-cart.js (/cart). Each lives
   * in its own asset to keep every file readable, and each reads the session
   * ONLY through this object.
   *
   * ⛔ READ-ONLY BY CONSTRUCTION. price() hands back a COPY, only of a real
   * number, only while the session is fresh, so nothing on the other side can
   * edit a figure that /cart would later echo to the order route. There is no
   * setter for a price, a discount or the session: acceptPriceChange() takes
   * only the ORDER route's own 409 body, and endSession() only ever takes a
   * session AWAY. A script on the page gains nothing here that it could not
   * already get by calling the API itself.
   * ------------------------------------------------------------------ */
  var claimedScopes = [];
  function isClaimedScope(el) { return !!el && claimedScopes.indexOf(el) !== -1; }

  var booted = false;
  var bootHooks = [];

  window.__rcdsDealerBridge = Object.freeze({
    version: 1,
    outcomes: Object.freeze({ DEALER: OUTCOME_DEALER, RETAIL: OUTCOME_RETAIL, UNKNOWN: OUTCOME_UNKNOWN }),
    maxIdsPerRead: MAX_IDS_PER_READ,
    freshMs: PRICE_FRESH_MS,
    load: function (ids, merge) { return loadPrices(ids, merge === true); },
    revalidate: function () { return revalidateAll(); },
    register: function (surface) {
      if (surface && typeof surface.refresh === 'function') surfaces.push({ refresh: surface.refresh });
    },
    onBoot: function (fn) {
      if (typeof fn !== 'function') return;
      if (booted) fn(); else bootHooks.push(fn);
    },
    fresh: function () { return hasFreshDealerPricing(); },
    price: function (vid) {
      if (!hasFreshDealerPricing()) return null;
      var p = rc.priceByVariant[vid];
      if (!isRealDealerPrice(p)) return null;
      return { msrp_cents: p.msrp_cents, dealer_price_cents: p.dealer_price_cents, base_cents: p.base_cents > 0 ? p.base_cents : 0,
        card_price_cents: p.card_price_cents > 0 ? p.card_price_cents : 0 };
    },
    // Asked in a dealer read inside the freshness window: priced, or known to
    // be unpriceable (the server omits a variant Shopify cannot price). Either
    // way, asking again before the window closes would only ask again.
    asked: function (vid) {
      var at = rc.askedAt[vid];
      return !!at && (Date.now() - at) <= PRICE_FRESH_MS;
    },
    discountPct: function () { return rc.discountPct; },
    isDealer: function () { return rc.isDealer === true; },
    hasMoneyFormat: function () { return hasMoneyFormat(); },
    fmt: function (cents) { return fmt(cents); },
    claim: function (scopeEl) {
      if (scopeEl && claimedScopes.indexOf(scopeEl) === -1) claimedScopes.push(scopeEl);
    },
    isClaimed: function (scopeEl) { return isClaimedScope(scopeEl); },
    // ---- what /cart needs besides prices (assets/rcds-dealer-cart.js) ----
    minOrderCents: function () { return hasFreshDealerPricing() ? rc.minOrderCents : 0; },
    gateNote: function () { return hasFreshDealerPricing() ? rc.gateNote : ''; },
    // { saves } while the server offers ACH or card on a fresh read, else null.
    cardPrice: function () { return (hasFreshDealerPricing() && rc.card) ? { saves: rc.card.saves } : null; },
    // A dealer confirmed by a read on THIS page, or the browser's seen-memory.
    // It decides a HOLD, never a price.
    dealerKnown: function () { return dealerConfirmedHere || hadDealerPricing(); },
    lapsedNotice: function (anchor, outcome) { paintLapsedNotice(anchor, outcome); },
    acceptPriceChange: function (body) { return acceptPriceChange(body); },
    endSession: function () { endDealerSession(); },
    setCartCount: function (n) { updateCartCounts(n); },
    ordersUrl: ORDERS_URL,
    portal: Object.freeze({ orders: PORTAL_ORDERS_URL, login: PORTAL_LOGIN_URL }),
    colors: Object.freeze({ err: COLOR_ERR, ok: COLOR_OK, muted: COLOR_MUTED }),
    memory: Object.freeze({ key: SEEN_KEY, windowMs: SEEN_MEMORY_MS, pendingClass: PENDING_CLASS, priceHoldClass: PRICE_HOLD_CLASS }),
    releasePriceHold: function () { releasePriceHold(); }
  });

  // A page with no cart dealer block has no retail checkout to hold, so the
  // <head> class has nothing to do there. On /cart it is the cart layer's to
  // take off, and if that layer never loads it stays: the side to fail on.
  function endPendingOffCart() {
    if (document.querySelector('[data-rcds-cart-dealer]')) return;
    var root = document.documentElement;
    if (root && root.classList) root.classList.remove(PENDING_CLASS);
  }

  function boot() {
    if (booted) return;
    booted = true;
    endPendingOffCart();
    document.querySelectorAll('[data-rcds-dealer-buybox]').forEach(bootBuyBox);
    // ⛔ IN THE SAME SYNCHRONOUS RUN AS THE SURFACES ABOVE, so the price
    // engine's and the cart layer's first reads join theirs (ONE READ PER PAGE).
    var hooks = bootHooks;
    bootHooks = [];
    hooks.forEach(function (fn) { try { fn(); } catch (e) { /* one hook must not stop the others */ } });
  }
  /*
   * ⛔ A DEFERRED SCRIPT WAITS FOR DOMContentLoaded. layout/theme.liquid loads
   * this file `defer`, and the price engine and the cart layer after it, also
   * `defer`. All run
   * while readyState is "interactive" and BEFORE DOMContentLoaded, so booting
   * the moment this file ran would read the page before the engine had even
   * executed, and the two would go out as two requests. Waiting for the event
   * lets both register first. `load` is a safety net for the one case the
   * event could already have passed; boot() runs once however it is reached.
   */
  var me = document.currentScript;
  var deferredParse = document.readyState === 'interactive' &&
    !!me && me.defer === true && me.async !== true;
  if (document.readyState === 'loading' || deferredParse) {
    document.addEventListener('DOMContentLoaded', boot);
    if (typeof window.addEventListener === 'function') window.addEventListener('load', boot);
  } else boot();
})();
